import type {Middleware} from '@reduxjs/toolkit';
import {clearSessionScopedCaches} from '../lib/sessionCleanup';
import {debugWarn} from '../lib/debug';

/**
 * Reset action for "the person at the keyboard is not who this state belongs to". The root reducer
 * in `store.ts` wipes every slice on it.
 */
export const SESSION_USER_CHANGED = 'session/userChanged';

/**
 * Every action that can put a *different* user into `auth`. All of them are how a session arrives:
 * a cold restore, a password sign-in, an emailed link, and the `onAuthStateChange` relay that fires
 * for token refreshes, other tabs, and sign-outs Supabase performs on its own.
 */
const SESSION_ACTIONS = new Set([
  'auth/initialize/fulfilled',
  'auth/signIn/fulfilled',
  'auth/updateSession/fulfilled',
  'auth/confirmEmailLink/fulfilled',
]);

/**
 * Purges the store whenever the signed-in user changes, not merely when someone clicks Log out.
 *
 * **Clearing on `signOut` alone is not enough**, which is why one account's project id was still
 * being sent after signing in as another. `signOut` is one of several ways a session ends or is
 * replaced, and the others never touch it:
 *
 *   - Supabase drops or swaps the session itself (refresh-token failure, sign-in in a second tab) and
 *     the only thing that happens here is an `onAuthStateChange` → `updateSession` with a new user.
 *   - An emailed login link calls `verifyOtp` and establishes a session over whatever was there.
 *   - `signOut` is fire-and-forget on the Supabase call and can lose a race with the next sign-in.
 *
 * In every one of those the store keeps the previous account's projects, transcriptions and
 * conversions, and the next project-scoped request goes out with an id the new user does not own —
 * `403 Unauthorized: You do not own this project`.
 *
 * Identity is the key, so the check is on the user id and nothing else. A session going to `null`
 * counts as a change too: state that needed a session is not allowed to outlive it.
 *
 * The cache purge lives here rather than in the reducer because it touches localStorage and
 * module-level maps — a reducer has to stay pure. The reducer's half is the `SESSION_USER_CHANGED`
 * case in `store.ts`.
 */
export const sessionIdentityMiddleware: Middleware = store => next => (action: any) => {
  if (!SESSION_ACTIONS.has(action?.type)) {
    return next(action);
  }

  const previousUserId = (store.getState() as any).auth?.user?.id ?? null;
  const incomingUserId = action.payload?.user?.id ?? null;

  // Nothing to protect until a user has actually been in the store.
  if (previousUserId && previousUserId !== incomingUserId) {
    debugWarn(
      '[sessionIdentity] Session changed user',
      `(${previousUserId} → ${incomingUserId ?? 'signed out'})`,
      '— dropping the previous session\'s state'
    );
    clearSessionScopedCaches();
    next({type: SESSION_USER_CHANGED});
  }

  return next(action);
};
